import React, { useDeferredValue, useState } from 'react';

import { useDebounce } from 'react-use';

import { IconFilterDial } from '@pkg/assets/icons';
import { FieldSearch, type FieldSearchProps, FilterChips, ToggleGrid } from '@pkg/components';
import { type Collectible } from '@pkg/graphql/codegen/graphql';
import { useAppSelector } from '@pkg/hooks';

import { useNFTsPageStore } from '@app/store/zustand';

import { FiltersModal } from './FiltersModal';

import {
    FiltersButton,
    FixedBottom,
    IconWrapper,
    SearchWrapper,
    SelectAllButton,
    StickyRow,
} from './FiltersMobile.style';

export interface FiltersMobileProps {
    nftEntries?: Collectible[];
    onSearch?: (searchTerm: string) => void;
}

// TODO: This prototype component contains some unimplemented filtering UI.
// Related to: `ProfilePage > NftFiltersRowProto`.
// Eventually, we may want to support what is in this component:
// - Search (restricted to this user's profile and respects visible/hidden privileges).
// - Sort by Rhythm Score.
// - Sort by other criteria (list price, rarity, etc).
// - Hide/Show sidebar toggle (opens a sidebar with more granular filtering UI).
// - Filter chips (allows access to quickly removing applied filters).
export function FiltersMobile({ nftEntries, onSearch }: FiltersMobileProps) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const deferredSearch = useDeferredValue(searchTerm);

    const isPageOwner = useAppSelector((state) => state.yatPage.isPageOwner);
    const { isListView, setIsListView, isSelectAll, handleSelectAll } = useNFTsPageStore();

    const handleChangeInput: FieldSearchProps['onChange'] = (newValue = '') => {
        setSearchTerm(newValue.toString());
    };

    useDebounce(
        () => {
            onSearch?.(deferredSearch);
        },
        200,
        [deferredSearch],
    );

    const userSpecificFilters = isPageOwner ? (
        <FixedBottom>
            <ToggleGrid selected={isListView} setSelected={setIsListView} />
            <SelectAllButton onAction={() => handleSelectAll(nftEntries || [])}>
                {isSelectAll ? 'Select None' : 'Select All'}
            </SelectAllButton>
        </FixedBottom>
    ) : null;

    return (
        <>
            <StickyRow>
                <SearchWrapper>
                    <FieldSearch
                        value={searchTerm}
                        placeholder="Search collection or token"
                        onChange={handleChangeInput}
                        backdropBlur
                    />
                </SearchWrapper>

                <FiltersButton onAction={() => setOpen(true)}>
                    <IconWrapper>
                        <IconFilterDial />
                    </IconWrapper>
                </FiltersButton>
            </StickyRow>

            {userSpecificFilters}
            <FilterChips filteredResultCount={nftEntries?.length} />
            <FiltersModal open={open} setOpen={setOpen} />
        </>
    );
}
